/** How one factor is named in an address.
 *
 * **The library gives a factor no id of its own.** `FactorLibraryFactorOut` has a publisher's
 * `external_id`, which is optional and is not unique across publishers, so it cannot be the route.
 * What identifies a row is what the library screen already keys it by (`FactorLibrary`'s React key):
 * the set, then the category, fuel or carrier, region and the unit it is priced per. The route is
 * those five parts, each percent-encoded and joined by `~`.
 *
 * **Looked up by building the same name, never by parsing it.** A part containing a `~` would
 * split wrongly, so nothing here splits the id: `findFactor` builds each row's id and compares.
 * An id nothing builds is simply not found.
 */

import type { FactorLibraryFactor, FactorLibrarySet } from "../api/factors";

type Identity = Pick<FactorLibraryFactor, "category" | "fuel_or_carrier" | "region" | "unit_in">;

export function factorRouteId(setKey: string, factor: Identity): string {
  return [setKey, factor.category, factor.fuel_or_carrier, factor.region, factor.unit_in]
    .map(encodeURIComponent)
    .join("~");
}

export function findFactor(
  sets: readonly FactorLibrarySet[],
  id: string,
): { set: FactorLibrarySet; factor: FactorLibraryFactor } | undefined {
  for (const set of sets) {
    const factor = set.factors.find((entry) => factorRouteId(set.key, entry) === id);
    if (factor) return { set, factor };
  }
  return undefined;
}
